import Flow, { Level } from '@digibee/flow';
import { useActor, useInterpret } from '@xstate/react';
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { AutoSizer, List } from 'react-virtualized';
import { createContext } from 'use-context-selector';
import { ActorRefFrom } from 'xstate';

import Node, { TreeNode } from './Node';
import treeMachine from './Tree.machine';
import useTreeContext from './utils/useTreeContext';
import { useRegisterActorOnEventBus } from '../../../../../../../../components/Bus/bus.machine';
import { goToComponent } from '../../../../helpers/goToComponent';

import {
  useComponentConfigStore,
  useTrackStore,
  useTriggerStore
} from '~/scenes/Build/scenes/FlowBuilder/stores';
import { generateFlowSpecFromStores } from '~/scenes/Build/scenes/FlowBuilder/stores/helpers';
import { isLinterEnabled } from '~/scenes/Build/scenes/FlowBuilder/stores/linterStore';

export type TreeActor = ActorRefFrom<typeof treeMachine>;

type slotRenderProps = {
  item: any;
};

type FlowTreeSlots = {
  actionsSlot?: (props: slotRenderProps) => JSX.Element;
};

export type FlowTreeContextType = FlowTreeSlots & {
  actor: TreeActor;
  mapConnectorImage: MapConnectorImage;
  onItemDoubleClick: (item: any) => void;
  goToLevel: (node: TreeNode<Level>) => void;
};

export type ShapeType = 'ellipse' | 'diamond' | 'round-pentagon' | 'hexagon';

export const FlowTreeContext = createContext<FlowTreeContextType>({
  actor: {} as TreeActor,
  mapConnectorImage: () => ({ shape: 'ellipse', image: 'ellipse' }),
  onItemDoubleClick: () => {},
  goToLevel: () => {}
});

export type MapConnectorImage = (
  item: string,
  type?: string
) => {
  shape: ShapeType;
  image: string;
};

type FlowTreeProps = FlowTreeSlots & {
  customFlow?: Flow;
  currentLevelName: string;
  onInit?: (actor: TreeActor) => any;
  mapConnectorImage: MapConnectorImage;
  goToLevel?: (node: TreeNode<Level>) => void;
  withoutStarterNode?: boolean;
  shouldInspectDebugLayout?: boolean;
  disableFloatActions?: boolean;
};

const Index = ({
  shouldInspectDebugLayout,
  disableFloatActions
}: {
  shouldInspectDebugLayout: boolean;
  disableFloatActions?: boolean;
}) => {
  const actor = useTreeContext(state => state.actor);
  const [state] = useActor(actor);
  const [scrollPosition, setScrolPosition] = useState(0);
  const { list } = state.context;

  const rowRenderer = useCallback(
    ({ index, style }: { index: number; style: CSSProperties }) => {
      const node = list[index];

      return (
        <div key={node.id} style={style}>
          <Node
            node={node}
            shouldInspectDebugLayout={shouldInspectDebugLayout}
            disableFloatActions={disableFloatActions}
          />
        </div>
      );
    },
    [list]
  );

  const handleScroll = useCallback(
    ({
      scrollTop,
      clientHeight,
      scrollHeight
    }: {
      clientHeight: number;
      scrollHeight: number;
      scrollTop: number;
    }) => {
      const isValidPosition =
        scrollTop + clientHeight < scrollHeight ||
        scrollTop === scrollHeight - clientHeight;
      const newScrollPosition = isValidPosition
        ? scrollTop
        : scrollHeight - clientHeight;
      setScrolPosition(newScrollPosition);
    },
    []
  );

  if (state.matches('loading')) return null;

  return (
    <AutoSizer>
      {({ height, width }) => (
        <List
          width={width}
          height={height}
          rowCount={list.length}
          rowHeight={shouldInspectDebugLayout ? 32 : 24}
          rowRenderer={rowRenderer}
          scrollTop={scrollPosition}
          onScroll={handleScroll}
        />
      )}
    </AutoSizer>
  );
};

const FlowTree = ({
  customFlow,
  currentLevelName,
  onInit,
  actionsSlot,
  mapConnectorImage = () => ({ shape: 'ellipse', image: '' }),
  goToLevel = () => {},
  withoutStarterNode = false,
  shouldInspectDebugLayout,
  disableFloatActions
}: FlowTreeProps) => {
  const usedFlow = useMemo(
    () => customFlow || new Flow(generateFlowSpecFromStores()),
    [customFlow]
  );

  const triggerSpec = useTriggerStore(state => state.trigger);

  const actor = useInterpret(treeMachine, {
    context: {
      flow: usedFlow,
      triggerSpec,
      withoutStarterNode
    }
  });

  useRegisterActorOnEventBus(actor);

  useEffect(() => {
    onInit?.(actor);
  }, []);

  useEffect(() => {
    if (!customFlow) return;

    actor.send({
      type: 'SET_FLOW',
      flow: usedFlow,
      levelName: currentLevelName
    });
  }, [usedFlow, customFlow]);

  useEffect(() => {
    if (customFlow) return;

    const updateFlow = () => {
      const flowspec = generateFlowSpecFromStores();
      actor.send({
        type: 'SET_FLOW',
        flow: new Flow(flowspec),
        levelName: currentLevelName
      });
    };

    const unsubscribeConfigs = useComponentConfigStore.subscribe(updateFlow);
    const unsubscribeTrack = useTrackStore.subscribe(updateFlow);

    return () => {
      unsubscribeConfigs();
      unsubscribeTrack();
    };
  }, [currentLevelName, customFlow]);

  useEffect(() => {
    actor.send({ type: 'SET_CURRENT_LEVEL', levelName: currentLevelName });
  }, [currentLevelName]);

  useEffect(() => {
    if (triggerSpec && isLinterEnabled())
      actor.send({ type: 'SET_TRIGGER_SPEC', triggerSpec });
  }, [triggerSpec]);

  return (
    <FlowTreeContext.Provider
      value={{
        actor,
        actionsSlot,
        mapConnectorImage,
        onItemDoubleClick: goToComponent,
        goToLevel
      }}
    >
      <Index
        shouldInspectDebugLayout={!!shouldInspectDebugLayout}
        disableFloatActions={disableFloatActions}
      />
    </FlowTreeContext.Provider>
  );
};

export default FlowTree;
